import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Injectable,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { File } from '@klickdata/core/file';
import { ResponseData } from '@klickdata/core/http';
import { MessageDeletedComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { GlobalFilterProperty, SelectFilterOption, TableSource } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { MatTableBase } from 'apps/klickdata/src/app/shared/table/mat-table-base';
import { filter, map, Observable, switchMap, takeUntil, tap } from 'rxjs';
import { K3FilesListingService } from '../k3-files-listing.service';
import { User } from '@klickdata/core/user';
import { AuthService } from '@klickdata/core/auth';
import { Clipboard } from '@angular/cdk/clipboard';
import { MediaService } from '@klickdata/core/media/src/media.service';

@Component({
    selector: 'app-files-listing-general',
    templateUrl: './files-listing-general.component.html',
    styleUrls: ['./files-listing-general.component.scss'],
    providers: [K3FilesListingService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class FilesListingGeneralComponent extends MatTableBase<File> implements AfterViewInit, OnChanges {
    @Input() file: File;
    @Input() public pageSize = 25;
    @Input() public columns = [
        'id',
        'title',
        'description',
        'tags',
        'visibility',
        'priority',
        'author',
        'updated_at',
        'favorite',
        'tools',
    ];
    @Input() visibility = 'private';
    @Input() cacheScope = null;
    AppScope = AppScope;
    public expandedRow: File | null;
    public filePrioOptions: SelectFilterOption[];
    GlobalFilterProperty = GlobalFilterProperty;
    public significantPrio = ['a1', 'a2', 'a3', 'b1', 'c3'];
    public filterFavorite = false;
    public authUser: User;
    constructor(
        protected auth: AuthService,
        protected listService: K3FilesListingService,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected message: MessageService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected downloadHelper: DownloadHelperService,
        protected mediaService: MediaService,
        protected clipboard: Clipboard
    ) {
        super(cdr, mobileService, listService);
        this.filePrioOptions = Utils.getTaskPrioOptions();
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
            });
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.route.queryParams.pipe(filter((params) => params.action === 'addFile')).subscribe(() =>
            setTimeout(() => {
                this.addFile();
                this.router.navigate([this.router.url.split('?')[0]]);
            }, 500)
        );
        this.listenToNewFile();
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['visibility'] &&
            changes['visibility'].currentValue !== 'users' &&
            changes['visibility'].currentValue !== 'groups'
        ) {
            // Insert 'shared_with' column after 'author' when visibility is not 'users' or 'groups'
            const authorIndex = this.columns.indexOf('author');
            if (authorIndex !== -1 && !this.columns.includes('shared_with')) {
                this.columns.splice(authorIndex + 1, 0, 'shared_with');
            }
        }
    }
    public onFilterFavoriteClick(): void {
        if (this.filterFavorite) {
            this.filter.createOrUpdateFilter([{ property: 'favorite', items: [this.authUser.id] }]);
        } else {
            this.filter.ignoreFilter(['favorite']);
        }
    }
    public favoriteResource(file: File) {
        this.listService
            .update({ id: file.id, favorite: !file.favorite }, 'user_feeds')
            .pipe(takeUntil(this.destroy))
            .subscribe((result) => {
                file.favorite = result.favorite;
                this.cdr.markForCheck();
            });
    }
    public listenToNewFile() {
        this.listService
            .listenToNewFile()
            .pipe(takeUntil(this.destroy))
            .subscribe((file) => {
                // Apply visibility filter when adding new file
                if (this.visibility !== 'users' && this.visibility !== 'groups') {
                    if (file.visibility !== 'users' && file.visibility !== 'groups') {
                        this.dataSource.replace(file);
                    }
                } else {
                    this.dataSource.replace(file);
                }
            });
    }

    public addFile(params?: { file?: File; visibility?: string; customer_id?: number }) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_FILE,
            data: {
                type: 'addFile',
                file: params?.file ?? new File({ id: null, visibility: params?.visibility ?? 'private' }),
                customer_id: params?.customer_id ?? null,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addFile'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                // if (data.visibility == this.visibility) {
                //     this.dataSource.add(new File(data.value));
                // }
                this.dataSource.refresh();
                this.cdr.markForCheck();
            });
    }

    onTagClick(tag: ResourceTag) {
        this.filter.filterByModel(tag.getFilter(), true);
    }

    updateFileTags(file: File, tagsIds: number[]) {
        this.listService
            .update({ id: file.id, tag_ids: tagsIds })
            .pipe(
                takeUntil(this.destroy),
                tap((file) => this.dataSource.refresh())
            )
            .subscribe();
    }
    onSelectPriority(file: File, prio: string) {
        this.listService
            .update({ id: file.id, priority: prio })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.dataSource.refresh())
            )
            .subscribe();
    }
    public download(file: File) {
        if (file.downloads) {
            this.downloadHelper.download(file.downloads);
        }
    }
    public delete(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete ${file.title}`,
                contentBody: $localize`You are about to delete ${file.title}. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.listService.destroy(file);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.dataSource.removeById(file.id);
                    this.message.openMessage(MessageDeletedComponent);
                }
            });
    }
    public createMaterial(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_RESOURCE,
            data: {
                typeId: ResourceTypes.DocMaterial,
                items: file.medias?.file?.map((mediaId) => ({ file_item_id: mediaId })),
            },
        });
    }
    public edit(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_FILE,
            data: {
                file: file,
                type: 'editFile',
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'editFile'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                //  if (data.visibility == this.visibility) {
                //             this.dataSource.replace(new File(data.value));
                //         }
                this.dataSource.refresh();
                this.cdr.markForCheck();
            });
    }
    /** Copies the public URL of every attachment, one per line (the download links carry an auth token). */
    public copyLink(file: File) {
        this.mediaService
            .getMediaByIds(file.medias.file)
            .pipe(takeUntil(this.destroy))
            .subscribe((medias) => {
                this.clipboard.copy(medias.map((media) => media.url).join('\n'));
                this.message.openMessage(MessageSavedComponent, $localize`Link copied to clipboard`);
            });
    }
    public getVisibilityTooltip(file: File) {
        return file.visibility == 'public'
            ? $localize`Public`
            : file.visibility == 'private'
            ? $localize`Private`
            : file.visibility == 'academy'
            ? $localize`Academy`
            : file.visibility == 'users'
            ? $localize`Learners`
            : $localize`Groups`;
    }
    public getManagersTooltip(managers: User[]): string {
        return managers.map((manager) => manager.fname + ' ' + manager.lname).join(', ');
    }
    public getManagersWithoutAuthor(file: File): User[] {
        return file.managers?.filter((manager) => manager.id !== this.authUser?.id);
    }
}
